import { combineReducers, configureStore, Reducer } from '@reduxjs/toolkit'
import { emptySplitApi as portalApi } from 'api/emptyApi'
import bevarSlice from 'Modules/Boardwork/Bevar/shared/store/bevarSlice'
import bblArchiveSlice from 'Modules/Boardwork/DocumentArchive/BblArchive/BblArchiveSlice'
import boardArchiveSlice from 'Modules/Boardwork/DocumentArchive/BoardArchive/BoardArchiveSlice'
import boardUpdateListSlice from 'Modules/Boardwork/DocumentArchive/BoardArchive/UpdateFileList/BoardUpdateListSlice'
import documentArchiveSlice from 'Modules/Boardwork/DocumentArchive/documentArchiveSlice'
import prosjektArkivSlice from 'Modules/Boardwork/DocumentArchive/ProsjektArkiv/ProsjektArkivSlice'
import uploadFilesSlice from 'Modules/Boardwork/DocumentArchive/UploadFIles/store/UploadFilesSlice'
import meetingsFiltersSlice from 'Modules/Boardwork/Meetings/shared/store/meetingsFilters'
import newOwnersFiltersSlice from 'Modules/Boardwork/Tasks/shared/store/newOwnersFilters'
import subleaseFiltersSlice from 'Modules/Boardwork/Tasks/Sublease/shared/store/subleaseFilters'
import contactsFiltersSlice from 'Modules/Communication/Contacts/shared/store/contactsFilters'
import composeEmailSlice from 'Modules/Communication/Emailv2/Compose/composeEmailSlice'
import noticesFiltersSlice from 'Modules/Communication/Notices/shared/store/noticesFilters'
import bankAccountsSlice from 'Modules/Economy/BankAccounts/store/BankAccountsSlice'
import budgetSlice from 'Modules/Economy/Budget/shared/store/budgetSlice'
import invoiceSlice from 'Modules/Economy/Invoice/shared/store/invoice.slice'
import loanSlice from 'Modules/Economy/Loan/shared/store/loanSlice'
import reimbursementSlice from 'Modules/Economy/Reimbursement/shared/reimbursementSlice'
import balanceAndTransactionsSlice from 'Modules/Economy/shared/balanceAndTransactionSlice'
import insuranceAgreementsFiltersSlice from 'Modules/HousingAssociation/Insurance/shared/store/insuranceAgreementsFilters'
import insuranceCasesFiltersSlice from 'Modules/HousingAssociation/Insurance/shared/store/insuranceCasesFilters'
import unitsFiltersSlice from 'Modules/HousingAssociation/OurCompany/shared/store/unitsFilters'
import tenantsFiltersSlice from 'Modules/HousingAssociation/Tenants/shared/store/tenantsFilters'
import publicationSlice from 'Modules/HousingCooperative/Publications/shared/store/publicationSlice'
import clientsReducer from './clients'
import CommunicationReducer from './communication'
import economyApi from './economy/economy.service'
import { emptySplitApi } from './emptyApi'
import guidesUiReducer from './guidesUi/slice'
import menuReducer from './menu'
import menuApi from './menu/menu.service'
import modalReducer from './modal'
import { reducer as NotificationsReducer } from './notifications'
import SendMessageReducer from './sendMessage'
import { SocketReducer } from './socket'
import { reducer as ToasterReducer } from './toaster'
import userApi from './user/user.service'

// emptySplitApi now lives in ./emptyApi to break the store <-> slice circular
// import. Re-exported here so existing `from 'shared/store/store'` imports that
// still reference it keep working.
export { emptySplitApi } from './emptyApi'

/**
 * Static reducers
 * Should exists over the whole application
 */
export let reducers = {
  guidesUi: guidesUiReducer,
  toaster: ToasterReducer,
  notifications: NotificationsReducer,
  socket: SocketReducer,
  sendMessage: SendMessageReducer,
  communication: CommunicationReducer,
  // Slices
  [modalReducer.name]: modalReducer.reducer,
  [menuReducer.name]: menuReducer.reducer,
  [clientsReducer.name]: clientsReducer.reducer,
  [publicationSlice.name]: publicationSlice.reducer,
  [loanSlice.name]: loanSlice.reducer,
  [budgetSlice.name]: budgetSlice.reducer,
  [bevarSlice.name]: bevarSlice.reducer,
  [reimbursementSlice.name]: reimbursementSlice.reducer,
  [bankAccountsSlice.name]: bankAccountsSlice.reducer,
  [balanceAndTransactionsSlice.name]: balanceAndTransactionsSlice.reducer,
  [bblArchiveSlice.name]: bblArchiveSlice.reducer,
  [composeEmailSlice.name]: composeEmailSlice.reducer,
  [boardArchiveSlice.name]: boardArchiveSlice.reducer,
  [documentArchiveSlice.name]: documentArchiveSlice.reducer,
  [prosjektArkivSlice.name]: prosjektArkivSlice.reducer,
  [boardUpdateListSlice.name]: boardUpdateListSlice.reducer,
  [uploadFilesSlice.name]: uploadFilesSlice.reducer,
  [invoiceSlice.name]: invoiceSlice.reducer,
  [insuranceAgreementsFiltersSlice.name]:
    insuranceAgreementsFiltersSlice.reducer,
  [insuranceCasesFiltersSlice.name]: insuranceCasesFiltersSlice.reducer,
  [contactsFiltersSlice.name]: contactsFiltersSlice.reducer,
  [noticesFiltersSlice.name]: noticesFiltersSlice.reducer,
  [unitsFiltersSlice.name]: unitsFiltersSlice.reducer,
  [tenantsFiltersSlice.name]: tenantsFiltersSlice.reducer,
  [meetingsFiltersSlice.name]: meetingsFiltersSlice.reducer,
  [subleaseFiltersSlice.name]: subleaseFiltersSlice.reducer,
  [newOwnersFiltersSlice.name]: newOwnersFiltersSlice.reducer,
  // APIs
  [economyApi.reducerPath]: economyApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [menuApi.reducerPath]: menuApi.reducer,
  [emptySplitApi.reducerPath]: emptySplitApi.reducer,
  [portalApi.reducerPath]: portalApi.reducer,
}

export const reduxMiddleware = [
  menuApi.middleware,
  economyApi.middleware,
  userApi.middleware,
  emptySplitApi.middleware,
  portalApi.middleware,
]

export const store = configureStore({
  reducer: combineReducers(reducers),
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({ serializableCheck: false }).concat(reduxMiddleware),
  devTools: process.env.NODE_ENV !== 'production',
})

/**
 * Init store
 */

export const registerReducer = (key: string, newReducer: Reducer) => {
  reducers = { ...reducers, [key]: newReducer }
  store.replaceReducer(combineReducers(reducers))
}

export const injectReducer = registerReducer

export type RootState = ReturnType<typeof store.getState>

export default store
